import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, message, Switch, ColorPicker, Row, Col } from 'antd';
import { useMutation } from '@apollo/client';
import { CREATE_TAG, UPDATE_TAG } from '../../mutations/tags';

const TagForm = ({ initialValues, onSuccess, isEditMode }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleSuccess = (response) => {
    if (response.errors && response.errors.length > 0) {
      message.error(response.errors[0]);
    } else {
      message.success(isEditMode ? 'Tag atualizada com sucesso!' : 'Tag criada com sucesso!');
      if (onSuccess && response.tag) {
        onSuccess(response.tag);
      }
      if (!isEditMode) {
        form.resetFields();
      }
    }
  };

  const handleError = (error) => {
    message.error('Erro ao salvar tag: ' + error.message);
  };

  // Mutation para criar tag
  const [createTag] = useMutation(CREATE_TAG, {
    onCompleted: (data) => handleSuccess(data.createTag),
    onError: handleError
  });

  // Mutation para atualizar tag
  const [updateTag] = useMutation(UPDATE_TAG, {
    onCompleted: (data) => handleSuccess(data.updateTag),
    onError: handleError
  });

  useEffect(() => {
    // Limpar o formulário quando o componente é montado ou quando initialValues muda
    form.resetFields();
    
    if (initialValues) {
      form.setFieldsValue({
        name: initialValues.name,
        active: initialValues.active,
        fontColor: initialValues.fontColor || '#000000',
        backgroundColor: initialValues.backgroundColor || '#6fa8dc'
      });
    }
  }, [initialValues, form, isEditMode]);

  const onFinish = async (values) => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    
    // Extrair valores de cor do ColorPicker
    const fontColor = typeof values.fontColor === 'string' 
      ? values.fontColor 
      : values.fontColor?.toHexString?.() || '#000000';
    
    const backgroundColor = typeof values.backgroundColor === 'string'
      ? values.backgroundColor
      : values.backgroundColor?.toHexString?.() || '#6fa8dc';
    
    try {
      if (isEditMode && initialValues) {
        await updateTag({ 
          variables: { 
            id: initialValues.id, 
            input: { 
              name: values.name,
              active: values.active,
              fontColor: fontColor,
              backgroundColor: backgroundColor
            } 
          } 
        });
      } else {
        await createTag({ 
          variables: { 
            input: { 
              name: values.name,
              active: values.active !== false,
              fontColor: fontColor,
              backgroundColor: backgroundColor
            } 
          } 
        });
      }
    } catch (error) {
      // Error UI is already handled by the mutation onError callback.
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ active: true, name: '', fontColor: '#000000', backgroundColor: '#6fa8dc' }}
    >
      <Form.Item
        name="name"
        label="Nome"
        rules={[{ required: true, message: 'Por favor, informe o nome da tag' }]}
      >
        <Input />
      </Form.Item>
      
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="fontColor"
            label="Cor da Fonte"
            rules={[{ required: true, message: 'Por favor, selecione a cor da fonte' }]}
          >
            <ColorPicker 
              format="hex"
              showText
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="backgroundColor"
            label="Cor de Fundo"
            rules={[{ required: true, message: 'Por favor, selecione a cor de fundo' }]}
          >
            <ColorPicker 
              format="hex"
              showText
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      
      {isEditMode && (
        <Form.Item
          name="active"
          label="Ativo"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      )}

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
          {isEditMode ? 'Atualizar' : 'Criar'}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default TagForm; 